
import { useState, useEffect, useRef } from 'react';
import TimelineItem from './components/TimelineItem';
import TimelineDetails from './components/TimelineDetails';
import Footer from '../../components/feature/Footer';

interface Period {
  years: string;
  position: string;
  focus: string;
  actions: string[];
  tags: string[];
}

interface Epoch {
  epoch: string;
  periods: Period[];
}

export default function Timeline() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const timelineRef = useRef<HTMLDivElement>(null);

  const timelineData: Epoch[] = [
    {
      epoch: "第一階段｜早期從政與財經基礎（1930–1948）",
      periods: [
        {
          years: "1930–1937",
          position: "福建省財政廳主計 / 廳長",
          focus: "田賦實物徵收制度",
          actions: [
            "制定《福建省田賦改徵實物或米折標準辦法》",
            "建立糧政平衡與物價穩定制度",
            "推行賦稅折色改制以恢復財政公平"
          ],
          tags: ["finance", "tax reform", "Fujian"]
        },
        {
          years: "1938–1945",
          position: "戰時華南財政官員",
          focus: "戰時財政與籌款制度",
          actions: [
            "實施『舉債、公賣、公營』三途籌款原則",
            "強調『政府有能』之積極財政思想",
            "建立抗戰期省級財政自足體系"
          ],
          tags: ["wartime economy", "public finance"]
        },
        {
          years: "1946–1948",
          position: "財政部主計司長",
          focus: "戰後財政重建",
          actions: [
            "整併中央稅制與預算體系",
            "倡導『實物平準、財政均衡』理念",
            "奠基臺灣接收後財政政策"
          ],
          tags: ["taxation", "budget system"]
        }
      ]
    },
    {
      epoch: "第二階段｜臺灣財政建設與幣制穩定（1949–1963）",
      periods: [
        {
          years: "1949–1952",
          position: "臺灣省財政廳長",
          focus: "臺灣幣制改革與財政穩定",
          actions: [
            "主導1949年幣制改革，確立新臺幣制度",
            "提出『雙平衡原則』（貿易與財政）",
            "實施黃金準備與外匯兌換制度"
          ],
          tags: ["currency reform", "monetary policy"]
        },
        {
          years: "1953–1958",
          position: "財政部部長",
          focus: "預算法制化與建設基金",
          actions: [
            "推行中央政府預算制度化",
            "建立公共建設基金原型",
            "整合國內外貸款來源"
          ],
          tags: ["budget reform", "infrastructure finance"]
        },
        {
          years: "1959–1963",
          position: "財政部長（續任）",
          focus: "出口導向與工業化政策",
          actions: [
            "規劃加工出口區設立方案",
            "提出『穩幣、增匯、促產、平物』四原則",
            "推動外匯平衡型經濟轉型"
          ],
          tags: ["industrial policy", "export zone"]
        }
      ]
    },
    {
      epoch: "第三階段｜行政院長與十大建設策源（1964–1972）",
      periods: [
        {
          years: "1964–1966",
          position: "行政院副院長",
          focus: "經建計畫推動",
          actions: [
            "執行第一次四年經建計畫",
            "推動教育與技職體系改革",
            "奠定後期建設整合體系"
          ],
          tags: ["economic planning", "education reform"]
        },
        {
          years: "1967–1969",
          position: "行政院長（初期）",
          focus: "產業與科技政策",
          actions: [
            "整合經建、交通與能源政策",
            "推動科學工業園區籌設案研究",
            "強化基礎建設投資結構"
          ],
          tags: ["infrastructure", "science policy"]
        },
        {
          years: "1970–1972",
          position: "行政院長（後期）",
          focus: "十大建設構想",
          actions: [
            "提出十大建設初步財政模型",
            "設計『以外匯盈餘支應國建』方案",
            "完成跨部會建設資金協調機制"
          ],
          tags: ["Ten Major Projects", "macro policy"]
        }
      ]
    },
    {
      epoch: "第四階段｜總統任期與外交經貿收束（1973–1981）",
      periods: [
        {
          years: "1973–1975",
          position: "副總統",
          focus: "行政與財政調和",
          actions: [
            "協調行政與立法預算程序",
            "穩定能源危機後財政局面",
            "推行糧食與能源儲備制度"
          ],
          tags: ["fiscal stability", "energy policy"]
        },
        {
          years: "1975–1978",
          position: "中華民國總統",
          focus: "十大建設全面執行",
          actions: [
            "推行重大交通與工業基建",
            "強化外貿與外交資金合作",
            "鞏固金融體系穩定"
          ],
          tags: ["presidency", "construction"]
        },
        {
          years: "1979–1981",
          position: "國策顧問",
          focus: "科學與技職教育推進",
          actions: [
            "指導新竹科學園區構想形成",
            "提出『科技導向經濟轉型』理論",
            "促成科技與教育政策結合"
          ],
          tags: ["science park", "education policy"]
        }
      ]
    }
  ];

  // 將所有時期展平為單一陣列
  const allPeriods = timelineData.flatMap(epoch => 
    epoch.periods.map((period, periodIndex) => ({
      ...period,
      epochTitle: epoch.epoch,
      globalIndex: timelineData.slice(0, timelineData.indexOf(epoch)).reduce((acc, e) => acc + e.periods.length, 0) + periodIndex
    }))
  );

  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;

      const timelineItems = timelineRef.current.querySelectorAll('[data-timeline-item]');
      const windowHeight = window.innerHeight;
      const newVisibleItems = new Set<number>();

      timelineItems.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.top + rect.height / 2;
        
        // 檢查項目是否在視窗中央區域
        if (itemCenter >= windowHeight * 0.3 && itemCenter <= windowHeight * 0.7) {
          newVisibleItems.add(index);
          if (activeIndex !== index) {
            setActiveIndex(index);
          }
        }
      });

      setVisibleItems(newVisibleItems);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初始檢查

    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeIndex]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3F0' }}>
      {/* Header */}
      <div className="py-20 text-center" style={{ backgroundColor: '#E8E4E0' }}>
        <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#8B7D6B' }}>
          嚴家淦編年史
        </h1>
        <p className="text-xl md:text-2xl max-w-4xl mx-auto px-6" style={{ color: '#A69B8A' }}>
          C.K. Yen Chronological Compendium
        </p>
        <p className="text-lg mt-4 max-w-3xl mx-auto px-6" style={{ color: '#B5A99A' }}>
          探索中華民國財政巨擘的政治生涯軌跡（1930-1981）
        </p>
      </div>

      {/* Timeline */}
      <div className="relative py-20" ref={timelineRef}>
        <div className="max-w-6xl mx-auto px-6">
          {/* 中央時間軸線 */}
          <div 
            className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full"
            style={{ backgroundColor: '#C4B5A0' }}
          ></div>

          {allPeriods.map((period, index) => (
            <TimelineItem
              key={index}
              period={period}
              index={index}
              isActive={activeIndex === index}
              isVisible={visibleItems.has(index)}
              isLeft={index % 2 === 0}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      </div>

      {/* 詳細資訊面板 */}
      {activeIndex !== null && (
        <TimelineDetails
          period={allPeriods[activeIndex]}
          onClose={() => setActiveIndex(null)}
        />
      )}

      <Footer />
    </div>
  );
}
